import { useState, useEffect, useCallback } from "react";
import * as React from "react";
import Cookies from "js-cookie";
import Styles from "./ThemeSelector.module.css";
import { useAppDispatch, useAppSelector } from "src/app/hooks";
import { setTheme, selectAuth } from "src/features/authSlice";
import { isSoneraHost } from "src/services/runtimeConfig";

const defaultTheme = "auto";

// One-time theme-cookie reset (Sonera host only). Bump this token whenever we
// change the Sonera default and want it to take effect for users who already
// picked a theme (their saved `theme` cookie would otherwise win forever). On
// the next visit to dnc.sonera.dev, each browser whose `themeReset` token doesn't
// match clears its saved `theme` once, then falls through to the default below.
const themeResetToken = "2026-06-sonera-default";

type ThemeKey =
  | "light"
  | "dark"
  | "midnight"
  | "sonera-light"
  | "sonera-dark"
  | "auto";

const ThemeSelector: React.FC = () => {
  const [selectedTheme, setSelectedTheme] = useState<ThemeKey>(defaultTheme);
  const { isSysadmin } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();

  // Pre-launch gating: the Sonera themes are only offered to sysadmins, or when
  // the app is served from a Sonera domain (sonera.dev / sonera.co). Everyone
  // else keeps the existing Light / Dark / Midnight / Auto set.
  const host =
    typeof window !== "undefined" ? window.location.hostname : "";
  const canUseSonera = !!isSysadmin || isSoneraHost(host);

  const isThemeIconKey = useCallback((value: string): value is ThemeKey => {
    return [
      "light",
      "dark",
      "midnight",
      "sonera-light",
      "sonera-dark",
      "auto",
    ].includes(value);
  }, []);

  // persist=false is used for the automatic default on load so we don't bake a
  // cookie the user never chose — that lets the "came from Sonera" default keep
  // re-evaluating each visit until they explicitly pick a theme.
  const handleThemeChange = useCallback(
    (themeValue: string, persist = true) => {
      if (persist) {
        Cookies.set("theme", themeValue, { expires: 365 * 10 });
      }
      if (isThemeIconKey(themeValue)) {
        setSelectedTheme(themeValue);
      }

      let themeAttribute = themeValue;
      if (themeValue === "auto") {
        themeAttribute = window.matchMedia("(prefers-color-scheme: dark)")
          .matches
          ? "dark"
          : "light";
      }
      document.body.setAttribute("data-theme", themeAttribute);
      dispatch(setTheme(themeAttribute));

      // Midnight and Sonera Dark are based off of the dark theme. Rather than
      // repeat all the dark mode variables for them, set the base theme to dark
      // and let each variant override just the colors it changes. Sonera Light
      // is a light variant, so it uses the default (none) base.
      if (
        themeAttribute === "midnight" ||
        themeAttribute === "dark" ||
        themeAttribute === "sonera-dark"
      ) {
        document.body.setAttribute("base-theme", "dark");
      } else {
        document.body.setAttribute("base-theme", "none");
      }
    },
    [isThemeIconKey, dispatch],
  );

  useEffect(() => {
    // One-time reset: clear any previously-saved theme once per token bump so the
    // Sonera default reaches users who already chose a theme. Scoped to the Sonera
    // host only — we never disturb saved themes on dnc.com / dncscrub.com. Runs
    // before we read the cookie so the cleared value falls through to the default.
    if (isSoneraHost(host) && Cookies.get("themeReset") !== themeResetToken) {
      Cookies.remove("theme");
      Cookies.set("themeReset", themeResetToken, { expires: 365 * 10 });
    }

    const cookie = Cookies.get("theme");
    let referrerHost = "";
    try {
      referrerHost = document.referrer
        ? new URL(document.referrer).hostname
        : "";
    } catch {
      referrerHost = "";
    }
    // Users arriving from the Sonera portal (or on the Sonera domain) default to
    // the Sonera theme.
    const cameFromSonera = isSoneraHost(referrerHost) || isSoneraHost(host);

    let initial: string;
    if (cookie && isThemeIconKey(cookie)) {
      initial = cookie;
    } else if (canUseSonera && cameFromSonera) {
      initial = "sonera-light";
    } else {
      initial = defaultTheme;
    }
    // Never apply a Sonera theme to someone who isn't allowed it (e.g. a stale
    // cookie after access changes).
    if (
      (initial === "sonera-light" || initial === "sonera-dark") &&
      !canUseSonera
    ) {
      initial = defaultTheme;
    }
    handleThemeChange(initial, false);
  }, [isThemeIconKey, handleThemeChange, canUseSonera, host]);

  const biIcons: Record<Exclude<ThemeKey, "sonera-light" | "sonera-dark">,
    string> = {
    light: "bi-sun-fill",
    dark: " bi-moon-fill",
    midnight: " bi-moon-stars-fill",
    auto: "bi-circle-half",
  };

  // The Sonera themes use the brand mark as their icon (Sonera Dark adds a
  // crescent moon); the others use Bootstrap icons.
  const renderIcon = (key: ThemeKey) => {
    if (key === "sonera-light" || key === "sonera-dark") {
      const src =
        key === "sonera-dark"
          ? "/images/sonera-mark-dark.svg"
          : "/images/sonera-mark.svg";
      return (
        // 1em box + -0.125em baseline shift matches the Bootstrap icon glyphs.
        <img
          src={src}
          alt=""
          style={{ height: "1em", width: "1em", verticalAlign: "-0.125em" }}
        />
      );
    }
    return <i className={`bi ${biIcons[key]}`}></i>;
  };

  return (
    <div className="nav-link nav-item dropdown d-flex p-0">
      <button
        className={`btn btn-link dropdown-toggle d-flex align-items-center text-white fs-6 ${Styles["theme-dropdown-button"]}`}
        id="bd-theme"
        type="button"
        aria-expanded="false"
        data-bs-toggle="dropdown"
        data-bs-display="static"
        aria-label="Theme"
      >
        {renderIcon(selectedTheme)}
        <span className="d-md-none ms-2" id="bd-theme-text">
          Toggle theme
        </span>
      </button>
      <ul
        className={`dropdown-menu dropdown-menu-end ${Styles["theme-selector-menu"]}`}
        aria-labelledby="bd-theme-text"
      >
        <li>
          <button
            type="button"
            className="dropdown-item d-flex align-items-center gap-2"
            data-bs-theme-value="light"
            aria-pressed={selectedTheme === "light"}
            onClick={() => handleThemeChange("light")}
          >
            {renderIcon("light")}
            Light
            {selectedTheme === "light" && <i className="bi bi-check2"></i>}
          </button>
        </li>
        <li>
          <button
            type="button"
            className="dropdown-item d-flex gap-2"
            data-bs-theme-value="dark"
            aria-pressed={selectedTheme === "dark"}
            onClick={() => handleThemeChange("dark")}
          >
            {renderIcon("dark")}
            Dark
            {selectedTheme === "dark" && <i className="bi bi-check2"></i>}
          </button>
        </li>
        <li>
          <button
            type="button"
            className="dropdown-item d-flex gap-2"
            data-bs-theme-value="midnight"
            aria-pressed={selectedTheme === "midnight"}
            onClick={() => handleThemeChange("midnight")}
          >
            {renderIcon("midnight")}
            Midnight
            {selectedTheme === "midnight" && <i className="bi bi-check2"></i>}
          </button>
        </li>
        {canUseSonera && (
          <>
            <li>
              <button
                type="button"
                className="dropdown-item d-flex gap-2"
                data-bs-theme-value="sonera-light"
                aria-pressed={selectedTheme === "sonera-light"}
                onClick={() => handleThemeChange("sonera-light")}
              >
                {renderIcon("sonera-light")}
                Sonera
                {selectedTheme === "sonera-light" && (
                  <i className="bi bi-check2"></i>
                )}
              </button>
            </li>
            <li>
              <button
                type="button"
                className="dropdown-item d-flex gap-2"
                data-bs-theme-value="sonera-dark"
                aria-pressed={selectedTheme === "sonera-dark"}
                onClick={() => handleThemeChange("sonera-dark")}
              >
                {renderIcon("sonera-dark")}
                Sonera Dark
                {selectedTheme === "sonera-dark" && (
                  <i className="bi bi-check2"></i>
                )}
              </button>
            </li>
          </>
        )}
        <li>
          <button
            type="button"
            className="dropdown-item d-flex gap-2"
            data-bs-theme-value="auto"
            aria-pressed={selectedTheme === "auto"}
            onClick={() => handleThemeChange("auto")}
          >
            {renderIcon("auto")}
            Auto
            {selectedTheme === "auto" && <i className="bi bi-check2"></i>}
          </button>
        </li>
      </ul>
    </div>
  );
};

export default ThemeSelector;
